import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Divider, ListItemButton, ListItemIcon, ListItemText, styled, useTheme, Tooltip, Box, SxProps, Theme } from '@mui/material';
import auth from 'services/auth';
import {
  Dashboard as DashboardIcon,
  NearMe as NearMeIcon,
  People as PeopleIcon,
  Draw as DrawIcon,
  Event as EventIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  DevicesOther as DevicesOtherIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'component'
})(({ theme }) => ({
  margin: theme.spacing(0.25, 1),
  borderRadius: 8,
  minHeight: 40,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  '&:hover': {
    backgroundColor: 'rgba(255, 0, 102, 0.06)'
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(255, 0, 102, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(255, 0, 102, 0.16)'
    }
  },
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    color: theme.palette.text.secondary
  },
  '&.Mui-selected .MuiListItemIcon-root': {
    color: theme.palette.primary.main
  }
}));

const StyledListItemIcon = styled(ListItemIcon)(() => ({
  minWidth: 36,
  '& .MuiSvgIcon-root': {
    fontSize: 20
  }
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    fontWeight: 500,
    fontSize: '0.875rem',
    color: theme.palette.text.primary
  },
  '.Mui-selected & .MuiListItemText-primary': {
    fontWeight: 600,
    color: theme.palette.primary.main
  }
}));

const CollapsedIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 8,
  margin: theme.spacing(0.5, 1)
}));

interface CollapsedLinkProps {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

const CollapsedLink: React.FC<CollapsedLinkProps> = ({ to, onClick, children, sx }) => (
  <Link to={to} onClick={onClick} style={{ textDecoration: 'none' }}>
    <CollapsedIcon sx={sx}>{children}</CollapsedIcon>
  </Link>
);

type NavItem = { path: string; label: string; icon: React.ReactNode; permission?: string };

const getVisibleItems = (items: NavItem[]) => items.filter((item) => !item.permission || auth.hasPermission(item.permission));

const isItemActive = (pathname: string, path: string) => {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
};

const NavItems = ({ items, isOpen, onClickLink }: { items: NavItem[]; isOpen: boolean; onClickLink: () => void }) => {
  const theme = useTheme();
  const location = useLocation();

  if (!isOpen) {
    return (
      <React.Fragment>
        {items.map((item) => {
          const selected = isItemActive(location.pathname, item.path);
          return (
            <Tooltip key={item.path} title={item.label} placement="right">
              <CollapsedLink
                to={item.path}
                onClick={onClickLink}
                sx={{
                  backgroundColor: selected ? 'rgba(255, 0, 102, 0.12)' : 'transparent',
                  color: selected ? theme.palette.primary.main : theme.palette.text.secondary
                }}
              >
                {item.icon}
              </CollapsedLink>
            </Tooltip>
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {items.map((item) => (
        <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
          <StyledListItemButton onClick={onClickLink} selected={isItemActive(location.pathname, item.path)}>
            <StyledListItemIcon>{item.icon}</StyledListItemIcon>
            <StyledListItemText primary={item.label} />
          </StyledListItemButton>
        </Link>
      ))}
    </React.Fragment>
  );
};

export const MainListItems = ({ onClickLink, isOpen }: { onClickLink: () => void; isOpen: boolean }) => {
  const items = getVisibleItems([
    { path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
    { path: '/my_events', label: 'Events', icon: <EventIcon />, permission: 'event:manage' },
    { path: '/entries', label: 'Entries', icon: <DrawIcon />, permission: 'entry:read' },
    { path: '/my_devices', label: 'Devices', icon: <NearMeIcon />, permission: 'device:read' },
    { path: '/users', label: 'Users', icon: <PeopleIcon />, permission: 'user:read' }
  ]);

  return <NavItems items={items} isOpen={isOpen} onClickLink={onClickLink} />;
};

export const AdminListItems = ({ onClickLink, isOpen }: { onClickLink: () => void; isOpen: boolean }) => {
  const items = getVisibleItems([
    { path: '/all_devices', label: 'All Devices', icon: <DevicesOtherIcon />, permission: 'device:admin' },
    { path: '/all_users', label: 'All Users', icon: <PeopleIcon />, permission: 'user:read_all' },
    { path: '/logs', label: 'Logs', icon: <ErrorIcon />, permission: 'log:read' },
    { path: '/configurations', label: 'Settings', icon: <SettingsIcon />, permission: 'configuration:manage' },
    { path: '/roles', label: 'Roles', icon: <SecurityIcon />, permission: 'role:read' }
  ]);

  if (!items.length) {
    return null;
  }

  return (
    <React.Fragment>
      <Divider sx={{ my: 1, mx: 1.5 }} />
      <NavItems items={items} isOpen={isOpen} onClickLink={onClickLink} />
    </React.Fragment>
  );
};
